import { useMemo } from 'react'
import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query'
import api from '@/services/api'
import type {
  BucketDaySummaryResponse,
  BucketRangeResponse,
} from '@/types/bucket'

// JST 1日分のバケット軽量メタ（ヒストグラム + スライダー初期化用）
export function useBucketDaySummary(
  forkId: string | undefined,
  date: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['bucket-day-summary', forkId, date],
    queryFn: async () => {
      const { data } = await api.get<BucketDaySummaryResponse>(
        `/forklifts/${forkId}/buckets/day-summary`,
        { params: { date } }
      )
      return data
    },
    enabled: (options?.enabled ?? true) && !!forkId && !!date,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  })
}

// 範囲のフルバケット（軌跡 + ログ）。30分境界に丸めた start/end を渡すこと。
//
// 1 日分（48 スロット）を 1 リクエストで取ると、終日稼働の機体では応答 JSON が
// Lambda の同期応答上限（6MB）を超えて 413 → 502 になり、軌跡もログも空になる。
// そのため範囲を 6 時間（12 スロット ≒ 1.6MB）ごとに分割して並列取得し、
// 呼び出し側には従来どおり 1 つの BucketRangeResponse として返す。
const SLOT_MS = 30 * 60 * 1000
const CHUNK_SLOTS = 12
const CHUNK_MS = SLOT_MS * CHUNK_SLOTS

function splitRange(
  start: string | undefined,
  end: string | undefined
): { start: string; end: string }[] {
  if (!start || !end) return []
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs)
    return []
  const chunks: { start: string; end: string }[] = []
  for (let t = startMs; t < endMs; t += CHUNK_MS) {
    chunks.push({
      start: new Date(t).toISOString(),
      end: new Date(Math.min(t + CHUNK_MS, endMs)).toISOString(),
    })
  }
  return chunks
}

export function useBucketRange(
  forkId: string | undefined,
  start: string | undefined,
  end: string | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && !!forkId && !!start && !!end
  const chunks = useMemo(() => splitRange(start, end), [start, end])

  const results = useQueries({
    queries: chunks.map((c) => ({
      queryKey: ['bucket-range', forkId, c.start, c.end],
      queryFn: async () => {
        const { data } = await api.get<BucketRangeResponse>(
          `/forklifts/${forkId}/buckets`,
          { params: { start: c.start, end: c.end } }
        )
        return data
      },
      enabled,
      staleTime: 1000 * 60 * 1,
      placeholderData: keepPreviousData,
    })),
  })

  // 全チャンクが揃ったときだけ結合結果を返す（部分的な軌跡を描かない）
  const data = useMemo<BucketRangeResponse | undefined>(() => {
    if (!forkId || !start || !end || chunks.length === 0) return undefined
    if (results.some((r) => !r.data)) return undefined
    const buckets = results
      .flatMap((r) => r.data!.buckets)
      .sort((a, b) => a.bucket_key.localeCompare(b.bucket_key))
    return { fork_id: forkId, start, end, buckets }
    // results は毎レンダー新しい配列になるため、各チャンクの data で依存を取る
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forkId, start, end, chunks, ...results.map((r) => r.data)])

  const isLoading = results.some((r) => r.isLoading)
  const isFetching = results.some((r) => r.isFetching)
  const isError = results.some((r) => r.isError)
  const error = results.find((r) => r.error)?.error ?? null

  return { data, isLoading, isFetching, isError, error }
}
